import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    MenuItem,
    TextField,
    Typography,
    CircularProgress,
    Divider,
    Popover,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slider,
    Tooltip,
    IconButton,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import {
    FormatBold,
    FormatItalic,
    FormatUnderlined,
    FormatListBulleted,
    FormatListNumbered,
    Title as TitleIcon,
    Image as ImageIcon,
    Link as LinkIcon,
    EmojiEmotions,
    FormatQuote,
    TableChart,
    HorizontalRule,
    FormatAlignLeft,
    FormatAlignCenter,
    FormatAlignRight,
    Crop,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Code as CodeIcon,
    SmartButton as SmartButtonIcon,
} from "@mui/icons-material";
import { createBlog, updateBlog, getBlogById, uploadBlogImage } from "../api/BlogApi";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getApiErrorMessage } from "../utils/apiError";

const TITLE_MAX = 150;

// recommended cover resolution; anything larger triggers the crop tool on upload
const COVER_RECO = { w: 1200, h: 675 };
const COVER_ASPECT = COVER_RECO.w / COVER_RECO.h; // 16:9

// inline content images: anything larger (either side) offers the crop tool
const CONTENT_RECO = { w: 1600, h: 1600 };

// reference sizes shown to the user near the image uploader
const RECOMMENDED_SIZES = [
    { use: "Blog Cover (Most Recommended)", size: "1200 × 675 px", ratio: "16:9" },
    { use: "Large Hero Banner", size: "1600 × 900 px", ratio: "16:9" },
    { use: "Social Sharing (OG)", size: "1200 × 630 px", ratio: "1.91:1" },
    { use: "Square Cards", size: "1200 × 1200 px", ratio: "1:1" },
    { use: "Portrait Feature", size: "1200 × 1600 px", ratio: "3:4" },
    { use: "In-Content Image (recommended)", size: "800 × 450 px", ratio: "16:9" },
    { use: "In-Content Image (maximum)", size: "1600 × 1600 px", ratio: "up to 1:1" },
];

// read a File's pixel dimensions without uploading it
const readImageDimensions = (file) =>
    new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            resolve({ w: img.naturalWidth, h: img.naturalHeight });
            URL.revokeObjectURL(url);
        };
        img.onerror = () => {
            resolve(null);
            URL.revokeObjectURL(url);
        };
        img.src = url;
    });

// curated emoji set for the picker
const EMOJIS = [
    "😀", "😁", "😂", "🤣", "😊", "😍", "😎", "🤩", "😉", "🙂",
    "😇", "🥳", "🤔", "🤗", "😴", "😅", "😭", "😡", "👍", "👎",
    "👏", "🙌", "🙏", "💪", "🤝", "👌", "✌️", "🔥", "✨", "⭐",
    "🎉", "🎊", "❤️", "🧡", "💛", "💚", "💙", "💜", "💯", "✅",
    "❌", "⚠️", "❓", "❗", "💡", "📌", "📝", "📚", "🏆", "🥇",
    "♟️", "♚", "♛", "♜", "♝", "♞", "♟", "🎯", "🚀", "🌟",
];

const toolbarBtn = {
    minWidth: 0,
    p: 1,
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
};

const activeBtn = {
    backgroundColor: "#f3e5f5",
    borderColor: "#9c27b0",
    color: "#9c27b0",
};

// reusable toolbar button; preventDefault on mousedown keeps the editor selection.
// Defined at module scope so its identity is stable across renders — otherwise the
// toolbar remounts on every state change and detaches picker anchor elements.
const TBtn = ({ active, onClick, title, disabled, children }) => (
    <Tooltip title={title || ""} arrow disableInteractive>
        <span>
            <Button
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={onClick}
                sx={{ ...toolbarBtn, ...(active ? activeBtn : {}) }}
            >
                {children}
            </Button>
        </span>
    </Tooltip>
);

// title -> url friendly slug
const slugify = (text) =>
    text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

/**
 * Simple crop modal: shows the image scaled to fit, with a draggable /
 * resizable crop rectangle. On confirm it renders the cropped region to a
 * canvas and hands back a Blob to be re-uploaded.
 */
const CropModal = ({ open, src, uploading, aspect, note, onCancel, onConfirm, onUseOriginal }) => {
    const imgRef = useRef(null);
    const dragState = useRef(null);
    const [crop, setCrop] = useState({ x: 0, y: 0, w: 0, h: 0 });
    const [tainted, setTainted] = useState(false);

    useEffect(() => {
        if (open) {
            setCrop({ x: 0, y: 0, w: 0, h: 0 });
            setTainted(false);
        }
    }, [open, src]);

    const onImgLoad = () => {
        const el = imgRef.current;
        if (!el) return;
        const w = el.clientWidth;
        const h = el.clientHeight;
        if (aspect) {
            // start with the largest centred box matching the target ratio
            let bw = w * 0.9;
            let bh = bw / aspect;
            if (bh > h * 0.9) {
                bh = h * 0.9;
                bw = bh * aspect;
            }
            setCrop({ x: (w - bw) / 2, y: (h - bh) / 2, w: bw, h: bh });
        } else {
            setCrop({ x: w * 0.1, y: h * 0.1, w: w * 0.8, h: h * 0.8 });
        }
    };

    // if the server does not send CORS headers, crossOrigin load fails; retry
    // without it so the image at least displays (export will then be blocked).
    const onImgError = () => {
        const el = imgRef.current;
        if (el && el.crossOrigin) {
            el.crossOrigin = null;
            el.src = src;
            setTainted(true);
        }
    };

    const onMove = (e) => {
        const ds = dragState.current;
        const el = imgRef.current;
        if (!ds || !el) return;
        const dx = e.clientX - ds.startX;
        const dy = e.clientY - ds.startY;
        const maxW = el.clientWidth;
        const maxH = el.clientHeight;
        setCrop(() => {
            const c = { ...ds.orig };
            if (ds.type === "move") {
                c.x = Math.min(Math.max(0, ds.orig.x + dx), maxW - ds.orig.w);
                c.y = Math.min(Math.max(0, ds.orig.y + dy), maxH - ds.orig.h);
            } else if (aspect) {
                let nw = Math.min(Math.max(30, ds.orig.w + dx), maxW - ds.orig.x);
                let nh = nw / aspect;
                if (ds.orig.y + nh > maxH) {
                    nh = maxH - ds.orig.y;
                    nw = nh * aspect;
                }
                c.w = nw;
                c.h = nh;
            } else {
                c.w = Math.min(Math.max(30, ds.orig.w + dx), maxW - ds.orig.x);
                c.h = Math.min(Math.max(30, ds.orig.h + dy), maxH - ds.orig.y);
            }
            return c;
        });
    };

    const endDrag = () => {
        dragState.current = null;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", endDrag);
    };

    const startDrag = (type) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragState.current = { type, startX: e.clientX, startY: e.clientY, orig: { ...crop } };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", endDrag);
    };

    const confirm = () => {
        const el = imgRef.current;
        if (!el || crop.w < 5 || crop.h < 5) return;
        const scaleX = el.naturalWidth / el.clientWidth;
        const scaleY = el.naturalHeight / el.clientHeight;
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(crop.w * scaleX));
        canvas.height = Math.max(1, Math.round(crop.h * scaleY));
        const ctx = canvas.getContext("2d");
        try {
            ctx.drawImage(
                el,
                crop.x * scaleX,
                crop.y * scaleY,
                crop.w * scaleX,
                crop.h * scaleY,
                0,
                0,
                canvas.width,
                canvas.height
            );
            canvas.toBlob((blob) => {
                if (!blob) {
                    alert("Could not crop this image.");
                    return;
                }
                onConfirm(blob);
            }, "image/png");
        } catch (err) {
            console.error("Crop export failed:", err);
            alert(
                "Unable to crop this image due to browser security (CORS) restrictions on the image host."
            );
        }
    };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                Crop Image
                <IconButton onClick={onCancel} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {note && (
                    <Typography sx={{ fontSize: 13, color: "#6a1b9a", mb: 1.5 }}>{note}</Typography>
                )}
                <Box
                    sx={{
                        position: "relative",
                        display: "inline-block",
                        maxWidth: "100%",
                        userSelect: "none",
                    }}
                >
                    <img
                        key={src}
                        ref={imgRef}
                        src={src}
                        alt="crop source"
                        // same-origin blob/data URLs never need (and shouldn't set)
                        // crossOrigin — only remote URLs attempt a CORS-clean load
                        crossOrigin={/^(blob:|data:)/i.test(src || "") ? undefined : "anonymous"}
                        onLoad={onImgLoad}
                        onError={onImgError}
                        style={{ maxWidth: "100%", maxHeight: "60vh", display: "block" }}
                        draggable={false}
                    />
                    {crop.w > 0 && (
                        <Box
                            onMouseDown={startDrag("move")}
                            sx={{
                                position: "absolute",
                                left: crop.x,
                                top: crop.y,
                                width: crop.w,
                                height: crop.h,
                                border: "2px dashed #fff",
                                boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                                cursor: "move",
                                boxSizing: "border-box",
                            }}
                        >
                            <Box
                                onMouseDown={startDrag("resize")}
                                sx={{
                                    position: "absolute",
                                    right: -8,
                                    bottom: -8,
                                    width: 16,
                                    height: 16,
                                    bgcolor: "#9c27b0",
                                    border: "2px solid #fff",
                                    borderRadius: "50%",
                                    cursor: "nwse-resize",
                                }}
                            />
                        </Box>
                    )}
                </Box>
                {tainted && (
                    <Typography sx={{ color: "#d32f2f", fontSize: 13, mt: 1 }}>
                        This image is served without CORS headers, so cropping may be blocked by the browser.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={uploading}>
                    Cancel
                </Button>
                {/* {onUseOriginal && (
                    <Button onClick={onUseOriginal} disabled={uploading}>
                        Use Full Image
                    </Button>
                )} */}
                <Button onClick={confirm} variant="contained" disabled={uploading}>
                    {uploading ? <CircularProgress size={20} color="inherit" /> : "Crop & Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// presets for button styles
const BUTTON_VARIANTS = {
    primary: {
        label: "Primary (Purple)",
        style: "background:#9c27b0;color:#ffffff;padding:10px 22px;border:none;border-radius:6px;font-size:15px;font-weight:600;cursor:pointer;display:inline-block;text-align:center;box-shadow:0 2px 4px rgba(0,0,0,0.15);",
        previewSx: {
            background: "#9c27b0",
            color: "#ffffff",
            padding: "10px 22px",
            border: "none",
            borderRadius: "6px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
        },
    },
    action: {
        label: "Action (Blue)",
        style: "background:#1e88e5;color:#ffffff;padding:10px 22px;border:none;border-radius:6px;font-size:15px;font-weight:600;cursor:pointer;display:inline-block;text-align:center;box-shadow:0 2px 4px rgba(0,0,0,0.15);",
        previewSx: {
            background: "#1e88e5",
            color: "#ffffff",
            padding: "10px 22px",
            border: "none",
            borderRadius: "6px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
        },
    },
    success: {
        label: "Success (Green)",
        style: "background:#2e7d32;color:#ffffff;padding:10px 22px;border:none;border-radius:6px;font-size:15px;font-weight:600;cursor:pointer;display:inline-block;text-align:center;box-shadow:0 2px 4px rgba(0,0,0,0.15);",
        previewSx: {
            background: "#2e7d32",
            color: "#ffffff",
            padding: "10px 22px",
            border: "none",
            borderRadius: "6px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
        },
    },
    outline: {
        label: "Outline",
        style: "background:transparent;color:#9c27b0;padding:8px 20px;border:2px solid #9c27b0;border-radius:6px;font-size:15px;font-weight:600;cursor:pointer;display:inline-block;text-align:center;",
        previewSx: {
            background: "transparent",
            color: "#9c27b0",
            padding: "8px 20px",
            border: "2px solid #9c27b0",
            borderRadius: "6px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
        },
    },
    razorpay: {
        label: "💳 Razorpay Button",
        isRazorpay: true,
    },
    custom: {
        label: "Custom HTML",
        style: "",
        previewSx: {},
    },
};

// decodes text/escaped button tags, forms, and embedded scripts into real HTML
const decodeButtonHtml = (html) => {
    if (!html) return "";

    let result = html;

    // 1. Convert escaped <a>...</a> wrapping buttons or in content
    result = result.replace(/(&lt;a[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*a\s*&gt;)/gi, (match, openTag, content) => {
        const cleanOpen = openTag.replace(/<[^>]+>/g, "");
        const decodedOpen = cleanOpen
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, "&")
            .trim();
        return `${decodedOpen}${content}</a>`;
    });

    // 2. Convert escaped or colored <form>...<script>...</script>...</form>
    result = result.replace(/(&lt;form[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*form\s*&gt;)/gi, (match, openTag, content) => {
        const cleanOpen = openTag.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
        return `${cleanOpen}${content}</form>`;
    });
    result = result.replace(/(&lt;script[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*script\s*&gt;)/gi, (match, openTag, content) => {
        const cleanOpen = openTag.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
        return `${cleanOpen}${content}</script>`;
    });

    // 3. Match any button pattern, whether escaped (&lt;button...) or raw (<button...)
    result = result.replace(/(&lt;button[\s\S]*?&gt;)([\s\S]*?)(&lt;\/\s*button\s*&gt;)/gi, (match, openTag, content) => {
        const cleanOpen = openTag.replace(/<[^>]+>/g, "");
        let decodedOpen = cleanOpen
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, "&")
            .trim();

        if (!/type\s*=/i.test(decodedOpen)) {
            decodedOpen = decodedOpen.replace(/^<button/i, '<button type="button"');
        }

        const cleanContent = content.replace(/^<\/span>/, "").replace(/<span[^>]*>$/, "");
        return `${decodedOpen}${cleanContent}</button>`;
    });

    // 4. Convert any remaining un-paired &lt;button...&gt;, &lt;form...&gt;, &lt;script...&gt;
    result = result.replace(/&lt;button[\s\S]*?&gt;/gi, (fullMatch) => {
        const clean = fullMatch.replace(/<[^>]+>/g, "");
        let decoded = clean
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, "&")
            .trim();
        if (!/type\s*=/i.test(decoded)) {
            decoded = decoded.replace(/^<button/i, '<button type="button"');
        }
        return decoded;
    });
    result = result.replace(/&lt;form[\s\S]*?&gt;/gi, (fullMatch) => {
        return fullMatch.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
    });
    result = result.replace(/&lt;script[\s\S]*?&gt;/gi, (fullMatch) => {
        return fullMatch.replace(/<[^>]+>/g, "").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&").trim();
    });
    result = result.replace(/&lt;\/\s*button\s*&gt;/gi, "</button>");
    result = result.replace(/&lt;\/\s*form\s*&gt;/gi, "</form>");
    result = result.replace(/&lt;\/\s*script\s*&gt;/gi, "</script>");
    result = result.replace(/&lt;\/\s*a\s*&gt;/gi, "</a>");

    // 5. Clean up any orphan spans that wrapped the tags
    result = result.replace(/<span[^>]*>\s*(<button[^>]*>[\s\S]*?<\/button>)\s*<\/span>/gi, "$1");
    result = result.replace(/<span[^>]*>\s*(<form[^>]*>[\s\S]*?<\/form>)\s*<\/span>/gi, "$1");
    result = result.replace(/<span[^>]*>\s*(<button[^>]*>)/gi, "$1");
    result = result.replace(/(<\/button>)\s*<\/span>/gi, "$1");
    result = result.replace(/<span[^>]*>\s*(<form[^>]*>)/gi, "$1");
    result = result.replace(/(<\/form>)\s*<\/span>/gi, "$1");

    // 6. Ensure any bare Razorpay script (not inside <form>) is wrapped in <form>...</form>
    result = result.replace(/(?:<form[^>]*>[\s\S]*?<\/form>)|(<script[^>]*payment-button\.js[^>]*>[\s\S]*?<\/script>)/gi, (m, bareScript) => {
        if (bareScript) {
            return `<form>${bareScript}</form>`;
        }
        return m;
    });

    return result;
};

// executes embedded <script> tags (like Razorpay payment-button.js) within a DOM container
const runEmbeddedScripts = (container) => {
    if (!container) return;

    // 1. Ensure all Razorpay scripts are inside a <form> before executing to prevent Razorpay alerts
    const rzpScripts = container.querySelectorAll("script[data-payment_button_id], script[src*='payment-button.js']");
    rzpScripts.forEach((script) => {
        if (!script.closest("form") || script.parentElement?.tagName !== "FORM") {
            const form = document.createElement("form");
            script.parentNode.insertBefore(form, script);
            form.appendChild(script);
        }
    });

    // 2. Execute Razorpay scripts inside forms
    const forms = container.querySelectorAll("form");
    forms.forEach((form) => {
        const script = form.querySelector("script");
        if (!script) return;
        // If a payment button was already injected by Razorpay in this form, do not re-run
        if (form.querySelector(".razorpay-payment-button")) {
            return;
        }
        if (script.getAttribute("data-executed") === "true") {
            return;
        }
        const newScript = document.createElement("script");
        Array.from(script.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
        });
        newScript.setAttribute("data-executed", "true");
        newScript.async = true;
        if (script.innerHTML) {
            newScript.appendChild(document.createTextNode(script.innerHTML));
        }
        if (script.parentNode) {
            script.parentNode.replaceChild(newScript, script);
        }
    });

    // 3. Execute any other non-Razorpay standalone scripts
    const standaloneScripts = container.querySelectorAll("script:not(form script)");
    standaloneScripts.forEach((oldScript) => {
        if (oldScript.getAttribute("data-executed") === "true") return;
        if (oldScript.src && oldScript.src.includes("payment-button.js")) return;
        if (oldScript.hasAttribute("data-payment_button_id")) return;

        const newScript = document.createElement("script");
        Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
        });
        newScript.setAttribute("data-executed", "true");
        newScript.async = true;
        if (oldScript.innerHTML) {
            newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        }
        if (oldScript.parentNode) {
            oldScript.parentNode.replaceChild(newScript, oldScript);
        }
    });
};

const CreateBlog = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const editorRef = useRef(null);
    const coverInputRef = useRef(null);
    const contentImageInputRef = useRef(null);
    const editorInitialized = useRef(false);
    const savedRangeRef = useRef(null);
    const selectedImgRef = useRef(null);
    const editingAnchorRef = useRef(null);
    // same-origin object URLs for content images added/cropped in this session,
    // keyed by the image's data-url (a stable string that survives node
    // replacement / re-renders). Re-cropping the remote (cross-origin, no-CORS)
    // signed URL taints the canvas and blocks export, so we crop these local
    // bytes instead. Tracked in a list too, to revoke them on unmount.
    const contentCropSrcRef = useRef(new Map());
    const objectUrlsRef = useRef([]);

    const [formData, setFormData] = useState({
        title: "",
        slug: "",
        status: "draft",
    });
    const [content, setContent] = useState("");
    const [coverImageUrl, setCoverImageUrl] = useState(""); // plain url -> sent on submit
    const [coverPreviewUrl, setCoverPreviewUrl] = useState(""); // signedUrl -> only for preview
    const [coverUploading, setCoverUploading] = useState(false);
    const [contentImageUploading, setContentImageUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);
    const [initialData, setInitialData] = useState(null);

    // rich-text editor UI state
    const [activeFormats, setActiveFormats] = useState({});
    const [emojiAnchor, setEmojiAnchor] = useState(null);
    const [tableAnchor, setTableAnchor] = useState(null);
    const [tableHover, setTableHover] = useState({ r: 0, c: 0 });
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const [linkText, setLinkText] = useState("");
    const [linkHasSelection, setLinkHasSelection] = useState(false);
    const [linkEditing, setLinkEditing] = useState(false);
    // popover shown when an existing link in the content is clicked
    const [linkPopover, setLinkPopover] = useState({ anchorEl: null, href: "" });
    const [selectedImg, setSelectedImg] = useState(null);
    const [imgWidth, setImgWidth] = useState(100);

    // Button popover and dialog states
    const [buttonPopover, setButtonPopover] = useState({
        anchorEl: null,
        btnEl: null,
        text: "",
        href: "",
        isRazorpay: false,
    });
    const [buttonDialogOpen, setButtonDialogOpen] = useState(false);
    const [buttonEditingEl, setButtonEditingEl] = useState(null);
    const [buttonText, setButtonText] = useState("Click Me!");
    const [buttonUrl, setButtonUrl] = useState("");
    const [buttonNewTab, setButtonNewTab] = useState(true);
    const [buttonVariant, setButtonVariant] = useState("primary");
    const [buttonCustomHtml, setButtonCustomHtml] = useState("");
    const [razorpayButtonId, setRazorpayButtonId] = useState("pl_TnQqnBysBUVrCG");
    const dialogPreviewRef = useRef(null);

    // unified crop controller: kind = 'content' (recrop inserted img) | 'cover' (crop before cover upload)
    const [cropState, setCropState] = useState({
        open: false,
        src: "",
        aspect: null,
        note: "",
        kind: null,
        file: null,
        objectUrl: null,
    });
    const [cropUploading, setCropUploading] = useState(false);
    // HTML source mode and custom HTML/button insertion states
    const [htmlMode, setHtmlMode] = useState(false);
    const [rawHtml, setRawHtml] = useState("");
    // bump to force hasChanges / disabled-state recompute after programmatic edits
    const [, setEditVersion] = useState(0);
    const touch = () => setEditVersion((v) => v + 1);

    const getReturnPage = () => {
        const params = new URLSearchParams(location.search);
        const queryPage = Number(params.get("returnPage"));
        const statePage = Number(location.state?.returnPage);
        const pageFromQuery = Number.isInteger(queryPage) && queryPage > 0 ? queryPage : null;
        const pageFromState = Number.isInteger(statePage) && statePage > 0 ? statePage : null;
        return pageFromQuery || pageFromState || 1;
    };

    const returnPage = getReturnPage();

    const removeSignedQuery = (url) => {
        if (!url) return url;
        return url.split("?")[0];
    };

    // in edit mode, load the existing blog and prefill the form
    const loadBlog = useCallback(async () => {
        try {
            setFetching(true);
            const res = await getBlogById(id);
            const blog = res?.data ?? res;
            if (!blog) return;

            setFormData({
                title: blog.title || "",
                slug: blog.slug || "",
                status: blog.status || "draft",
                slugEdited: true, // don't auto-overwrite an existing slug
            });

            const wrapper = document.createElement("div");
            wrapper.innerHTML = decodeButtonHtml(blog.content || "");

            wrapper.querySelectorAll("img").forEach((img) => {
                const signed = img.getAttribute("src");
                img.setAttribute("data-url", removeSignedQuery(signed));
                img.setAttribute("data-existing", "true");
            });

            const html = wrapper.innerHTML;
            setContent(html);
            editorInitialized.current = false;
            if (blog.coverImageUrl) {
                setCoverImageUrl(removeSignedQuery(blog.coverImageUrl));
                setCoverPreviewUrl(blog.coverImageUrl);
            }

            setInitialData({
                title: blog.title || "",
                slug: blog.slug || "",
                status: blog.status || "draft",
                coverImageUrl: removeSignedQuery(blog.coverImageUrl || ""),
                content: getNormalizedHtml(html),
            });
        } catch (error) {
            console.error("Failed to load blog:", error);
            alert(getApiErrorMessage(error, "Failed to load blog ❌"));
        } finally {
            setFetching(false);
        }
    }, [id]);

    const getNormalizedHtml = (html = "") => {
        const div = document.createElement("div");
        div.innerHTML = html;

        div.querySelectorAll("img").forEach((img) => {
            const src = img.getAttribute("src");
            if (src) {
                img.setAttribute("src", removeSignedQuery(src));
            }
        });

        return div.innerHTML.trim();
    };

    useEffect(() => {
        if (editorRef.current && content && !editorInitialized.current) {
            editorRef.current.innerHTML = content;
            editorInitialized.current = true;
            setTimeout(() => {
                runEmbeddedScripts(editorRef.current);
            }, 50);
            touch();
        }
    }, [content, fetching]);

    useEffect(() => {
        editorInitialized.current = false;

        if (isEditMode) {
            loadBlog();
        }
    }, [isEditMode, loadBlog]);

    // re-run embedded scripts in the live preview whenever Razorpay dialog opens or button ID changes
    useEffect(() => {
        if (buttonDialogOpen && buttonVariant === "razorpay" && dialogPreviewRef.current) {
            const timer = setTimeout(() => {
                runEmbeddedScripts(dialogPreviewRef.current);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [buttonDialogOpen, buttonVariant, razorpayButtonId]);

    // revoke any crop-source object URLs created this session on unmount.
    // read the ref at cleanup time — cacheCropSource replaces the array.
    useEffect(() => {
        const ref = objectUrlsRef;
        return () => {
            ref.current.forEach((u) => URL.revokeObjectURL(u));
        };
    }, []);

    // track which formatting commands are active for the current selection
    const updateActiveFormats = useCallback(() => {
        if (!editorRef.current) return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        if (!editorRef.current.contains(sel.anchorNode)) return;

        let block = "";
        try {
            block = (document.queryCommandValue("formatBlock") || "").toLowerCase();
        } catch (e) {
            block = "";
        }
        const q = (cmd) => {
            try {
                return document.queryCommandState(cmd);
            } catch (e) {
                return false;
            }
        };
        setActiveFormats({
            bold: q("bold"),
            italic: q("italic"),
            underline: q("underline"),
            ul: q("insertUnorderedList"),
            ol: q("insertOrderedList"),
            justifyLeft: q("justifyLeft"),
            justifyCenter: q("justifyCenter"),
            justifyRight: q("justifyRight"),
            h1: block === "h1",
            blockquote: block === "blockquote",
        });
    }, []);

    useEffect(() => {
        document.addEventListener("selectionchange", updateActiveFormats);
        return () => document.removeEventListener("selectionchange", updateActiveFormats);
    }, [updateActiveFormats]);

    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount && editorRef.current?.contains(sel.anchorNode)) {
            savedRangeRef.current = sel.getRangeAt(0).cloneRange();
        }
    };

    const restoreSelection = () => {
        editorRef.current?.focus();
        const sel = window.getSelection();
        if (savedRangeRef.current) {
            sel.removeAllRanges();
            sel.addRange(savedRangeRef.current);
        }
    };

    const handleTitleChange = (e) => {
        const value = e.target.value;
        setFormData((prev) => ({
            ...prev,
            title: value,
            // keep slug in sync with title unless the user edited it manually
            slug: prev.slugEdited ? prev.slug : slugify(value),
        }));
        if (value.trim() && value.length <= TITLE_MAX) {
            setErrors((prev) => ({ ...prev, title: "" }));
        }
    };

    const handleSlugChange = (e) => {
        const value = slugify(e.target.value);
        setFormData((prev) => ({ ...prev, slug: value, slugEdited: true }));
    };

    // sync content state from the editable div
    const handleContentInput = () => {
        const text = editorRef.current?.textContent?.trim() || "";
        const html = editorRef.current?.innerHTML?.trim() || "";
        if (text || (html && html !== "<p><br></p>" && html !== "<br>")) {
            setErrors((prev) => ({ ...prev, content: "" }));
        }
        touch();
    };

    // toggle between WYSIWYG editor and raw HTML source view
    const toggleHtmlMode = () => {
        if (!htmlMode) {
            const currentHtml = editorRef.current?.innerHTML || "";
            // clean out runtime injected razorpay button so rawHtml shows clean markup
            const temp = document.createElement("div");
            temp.innerHTML = decodeButtonHtml(currentHtml);
            temp.querySelectorAll("form").forEach((form) => {
                const script = form.querySelector("script[data-payment_button_id]");
                if (script) {
                    form.querySelectorAll(".razorpay-payment-button").forEach((b) => b.remove());
                }
            });
            temp.querySelectorAll("script").forEach((s) => s.removeAttribute("data-executed"));
            setRawHtml(temp.innerHTML);
            setHtmlMode(true);
        } else {
            const updatedHtml = rawHtml;
            setHtmlMode(false);
            setTimeout(() => {
                if (editorRef.current) {
                    editorRef.current.innerHTML = decodeButtonHtml(updatedHtml);
                    runEmbeddedScripts(editorRef.current);
                    handleContentInput();
                }
            }, 0);
        }
    };

    const handleRawHtmlChange = (e) => {
        const val = e.target.value;
        setRawHtml(val);
        if (val.trim()) {
            setErrors((prev) => ({ ...prev, content: "" }));
        }
        touch();
    };

    // open Add Button dialog
    const openAddButtonDialog = () => {
        saveSelection();
        setButtonEditingEl(null);
        setButtonText("Click Me!");
        setButtonUrl("");
        setButtonNewTab(true);
        setButtonVariant("primary");
        setRazorpayButtonId("pl_TnQqnBysBUVrCG");
        setButtonCustomHtml('<button type="button" style="background:#9c27b0;color:#fff;padding:10px 22px;border:none;border-radius:6px;font-weight:600;cursor:pointer;">Click Me!</button>');
        setButtonPopover({ anchorEl: null, btnEl: null, text: "", href: "", isRazorpay: false });
        setButtonDialogOpen(true);
    };

    // open Edit Button dialog from popover
    const openEditButton = (btn) => {
        if (!btn) return;
        setButtonEditingEl(btn);
        const form = btn.closest("form");
        const rzpScript = form ? form.querySelector("script[data-payment_button_id]") : null;
        const isRzp = Boolean(rzpScript || btn.classList.contains("razorpay-payment-button"));

        if (isRzp) {
            setButtonVariant("razorpay");
            const buttonId = rzpScript?.getAttribute("data-payment_button_id") || "pl_TnQqnBysBUVrCG";
            setRazorpayButtonId(buttonId);
            setButtonPopover({ anchorEl: null, btnEl: null, text: "", href: "", isRazorpay: false });
            setButtonDialogOpen(true);
            return;
        }

        const anchor = btn.closest("a") || (btn.parentNode?.tagName === "A" ? btn.parentNode : null);
        const text = btn.textContent?.trim() || "Click Me!";
        const href = anchor ? anchor.getAttribute("href") || "" : "";
        const target = anchor ? anchor.getAttribute("target") || "" : "";
        setButtonText(text);
        setButtonUrl(href);
        setButtonNewTab(target === "_blank" || !target);

        const style = btn.getAttribute("style") || "";
        if (style.includes("#1e88e5")) {
            setButtonVariant("action");
        } else if (style.includes("#2e7d32")) {
            setButtonVariant("success");
        } else if (style.includes("transparent") || style.includes("border: 2px") || style.includes("border:2px")) {
            setButtonVariant("outline");
        } else if (style.includes("#9c27b0") || !style) {
            setButtonVariant("primary");
        } else {
            setButtonVariant("custom");
            setButtonCustomHtml(anchor ? anchor.outerHTML : btn.outerHTML);
        }

        setButtonPopover({ anchorEl: null, btnEl: null, text: "", href: "", isRazorpay: false });
        setButtonDialogOpen(true);
    };

    const removeButton = (btn) => {
        const target = btn || buttonPopover.btnEl;
        if (target && target.parentNode) {
            const form = target.closest("form");
            const anchor = target.closest("a") || (target.parentNode?.tagName === "A" ? target.parentNode : null);
            const targetToRemove = form || anchor || target;
            if (targetToRemove && targetToRemove.parentNode) {
                targetToRemove.parentNode.removeChild(targetToRemove);
            }
            handleContentInput();
        }
        setButtonPopover({ anchorEl: null, btnEl: null, text: "", href: "", isRazorpay: false });
    };

    const applyButton = () => {
        let snippet = "";
        if (buttonVariant === "razorpay") {
            let cleanId = razorpayButtonId.trim();
            const match = cleanId.match(/data-payment_button_id=["']([^"']+)["']/i);
            if (match) cleanId = match[1];
            cleanId = cleanId || "pl_TnQqnBysBUVrCG";
            snippet = `<form><script src="https://checkout.razorpay.com/v1/payment-button.js" data-payment_button_id="${cleanId}" async> </script></form><p><br/></p>`;
        } else if (buttonVariant === "custom") {
            snippet = buttonCustomHtml.trim();
        } else {
            const style = BUTTON_VARIANTS[buttonVariant]?.style || BUTTON_VARIANTS.primary.style;
            const text = buttonText.trim() || "Click Me!";
            let url = buttonUrl.trim();
            if (url) {
                if (!/^https?:\/\//i.test(url) && !url.startsWith("mailto:") && !url.startsWith("/")) {
                    url = `https://${url}`;
                }
                const targetAttr = buttonNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
                snippet = `<a href="${url}"${targetAttr} style="text-decoration:none;"><button type="button" style="${style}">${text}</button></a>`;
            } else {
                snippet = `<button type="button" style="${style}">${text}</button>`;
            }
        }

        if (!snippet) {
            setButtonDialogOpen(false);
            return;
        }

        if (buttonEditingEl) {
            const form = buttonEditingEl.closest("form");
            const anchor = buttonEditingEl.closest("a") || (buttonEditingEl.parentNode?.tagName === "A" ? buttonEditingEl.parentNode : null);
            const targetEl = form || anchor || buttonEditingEl;
            if (targetEl && targetEl.parentNode) {
                const temp = document.createElement("div");
                temp.innerHTML = snippet;
                const newEl = temp.firstElementChild;
                if (newEl) {
                    targetEl.parentNode.replaceChild(newEl, targetEl);
                    setTimeout(() => {
                        runEmbeddedScripts(editorRef.current);
                    }, 50);
                    handleContentInput();
                }
            }
        } else if (htmlMode) {
            setRawHtml((prev) => (prev ? `${prev}\n${snippet}` : snippet));
            setErrors((prev) => ({ ...prev, content: "" }));
            touch();
        } else {
            restoreSelection();
            document.execCommand("insertHTML", false, snippet);
            setTimeout(() => {
                runEmbeddedScripts(editorRef.current);
            }, 50);
            handleContentInput();
        }

        setButtonDialogOpen(false);
        setButtonEditingEl(null);
    };

    // smart paste: if user pastes button HTML or script tags, decode and insert as visual button
    const handlePaste = (e) => {
        const text = e.clipboardData?.getData("text/plain") || "";
        const html = e.clipboardData?.getData("text/html") || "";
        const contentToCheck = html || text;

        if (/<button|<form|<script|payment-button|data-payment_button_id|&lt;button|&lt;form|&lt;script/i.test(contentToCheck)) {
            e.preventDefault();
            const decoded = decodeButtonHtml(contentToCheck);
            document.execCommand("insertHTML", false, decoded);
            setTimeout(() => {
                runEmbeddedScripts(editorRef.current);
            }, 50);
            handleContentInput();
        }
    };

    const exec = (command, value = null) => {
        editorRef.current?.focus();
        document.execCommand(command, false, value);
        updateActiveFormats();
        handleContentInput();
    };

    // toggle a block format (h1 / blockquote) back to a paragraph if re-applied
    const toggleBlock = (tag) => {
        let current = "";
        try {
            current = (document.queryCommandValue("formatBlock") || "").toLowerCase();
        } catch (e) {
            current = "";
        }
        const bare = tag.replace(/[<>]/g, "");
        exec("formatBlock", current === bare ? "<p>" : tag);
    };

    // ---- link ----
    // find the <a> that contains a node (or the node itself), scoped to the editor
    const findAnchor = (node) => {
        if (!node) return null;
        const el = node.nodeType === 1 ? node : node.parentElement;
        const anchor = el ? el.closest("a") : null;
        return anchor && editorRef.current?.contains(anchor) ? anchor : null;
    };

    // open the dialog prefilled to edit an existing anchor
    const openEditLink = (anchor) => {
        if (!anchor) return;
        editingAnchorRef.current = anchor;
        setLinkEditing(true);
        setLinkHasSelection(false); // allow editing the display text too
        setLinkText(anchor.textContent || "");
        setLinkUrl(anchor.getAttribute("href") || "");
        setLinkPopover({ anchorEl: null, href: "" });
        setLinkOpen(true);
    };

    const openLinkDialog = () => {
        saveSelection();
        const sel = window.getSelection();
        // if the caret sits inside an existing link, edit it instead of creating one
        const anchor = findAnchor(sel?.anchorNode);
        if (anchor) {
            openEditLink(anchor);
            return;
        }
        editingAnchorRef.current = null;
        setLinkEditing(false);
        const selectedText = sel ? sel.toString() : "";
        const hasSelection = selectedText.length > 0;
        setLinkHasSelection(hasSelection);
        setLinkText(selectedText);
        setLinkUrl("");
        setLinkOpen(true);
    };

    const closeLinkDialog = () => {
        setLinkOpen(false);
        editingAnchorRef.current = null;
        setLinkEditing(false);
    };

    // remove the link wrapper but keep its text
    const removeLink = (anchor) => {
        const target = anchor || editingAnchorRef.current;
        if (target && target.parentNode) {
            const parent = target.parentNode;
            while (target.firstChild) parent.insertBefore(target.firstChild, target);
            parent.removeChild(target);
            handleContentInput();
        }
        setLinkPopover({ anchorEl: null, href: "" });
        closeLinkDialog();
    };

    const applyLink = () => {
        let url = linkUrl.trim();
        if (!url) {
            closeLinkDialog();
            return;
        }
        if (!/^https?:\/\//i.test(url) && !url.startsWith("mailto:") && !url.startsWith("/")) {
            url = `https://${url}`;
        }

        // editing an existing anchor: update it in place
        const editing = editingAnchorRef.current;
        if (editing) {
            editing.setAttribute("href", url);
            editing.setAttribute("target", "_blank");
            editing.setAttribute("rel", "noopener noreferrer");
            const newText = linkText.trim();
            if (newText && newText !== editing.textContent) {
                editing.textContent = newText;
            }
            closeLinkDialog();
            updateActiveFormats();
            handleContentInput();
            return;
        }

        restoreSelection();
        const sel = window.getSelection();
        const hasSelection = sel && sel.toString().length > 0;
        const safeUrl = url.replace(/"/g, "&quot;");
        if (hasSelection) {
            document.execCommand("createLink", false, url);
            // add target/rel to the anchor that now wraps the selection
            const anchorNode = sel.anchorNode;
            const anchorEl =
                anchorNode?.nodeType === 1 ? anchorNode.closest("a") : anchorNode?.parentElement?.closest("a");
            if (anchorEl) {
                anchorEl.setAttribute("target", "_blank");
                anchorEl.setAttribute("rel", "noopener noreferrer");
            }
        } else {
            const text = (linkText.trim() || url).replace(/</g, "&lt;");
            document.execCommand(
                "insertHTML",
                false,
                `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`
            );
        }
        closeLinkDialog();
        updateActiveFormats();
        handleContentInput();
    };

    // ---- emoji ----
    const openEmoji = (e) => {
        saveSelection();
        setEmojiAnchor(e.currentTarget);
    };

    const insertEmoji = (emoji) => {
        restoreSelection();
        document.execCommand("insertText", false, emoji);
        setEmojiAnchor(null);
        handleContentInput();
    };

    // ---- table ----
    const openTable = (e) => {
        saveSelection();
        setTableHover({ r: 0, c: 0 });
        setTableAnchor(e.currentTarget);
    };

    const insertTable = (rows, cols) => {
        let html =
            '<table style="border-collapse:collapse;width:100%;margin:12px 0;" border="1">';
        for (let r = 0; r < rows; r++) {
            html += "<tr>";
            for (let c = 0; c < cols; c++) {
                html +=
                    '<td style="border:1px solid #ccc;padding:8px;min-width:40px;">&nbsp;</td>';
            }
            html += "</tr>";
        }
        html += "</table><p><br/></p>";
        restoreSelection();
        document.execCommand("insertHTML", false, html);
        setTableAnchor(null);
        handleContentInput();
    };

    // ---- page break ----
    const insertPageBreak = () => {
        editorRef.current?.focus();
        document.execCommand(
            "insertHTML",
            false,
            '<hr class="page-break" style="page-break-after:always;break-after:page;border:none;border-top:2px dashed #9c27b0;margin:20px 0;" /><p><br/></p>'
        );
        handleContentInput();
    };

    // ---- image selection & editing ----
    const clearImgSelection = () => {
        if (selectedImgRef.current) {
            selectedImgRef.current.style.outline = "";
        }
        selectedImgRef.current = null;
        setSelectedImg(null);
    };

    const handleEditorClick = (e) => {
        // if clicking a button or form inside the editor, open button popover and prevent form submission / navigation
        const btn = typeof e.target.closest === "function" ? e.target.closest("button") : null;
        const form = typeof e.target.closest === "function" ? e.target.closest("form") : null;
        const targetBtn = btn || (form ? form.querySelector("button") : null);

        if ((btn || form) && editorRef.current?.contains(btn || form)) {
            e.preventDefault();
            e.stopPropagation();
            const activeEl = btn || form;
            const rzpScript = form ? form.querySelector("script[data-payment_button_id]") : null;
            const isRzp = Boolean(rzpScript || activeEl.classList.contains("razorpay-payment-button"));

            if (btn && !btn.getAttribute("type")) {
                btn.setAttribute("type", "button");
            }
            const anchor = btn ? (btn.closest("a") || (btn.parentNode?.tagName === "A" ? btn.parentNode : null)) : null;
            const rzpId = rzpScript?.getAttribute("data-payment_button_id") || "";

            setButtonPopover({
                anchorEl: activeEl,
                btnEl: targetBtn || activeEl,
                text: isRzp ? `Razorpay Payment (${rzpId || "pl_..."})` : (targetBtn?.textContent || "Button"),
                href: anchor ? anchor.getAttribute("href") || "" : "",
                isRazorpay: isRzp,
            });
            return;
        } else if (buttonPopover.anchorEl) {
            setButtonPopover({ anchorEl: null, btnEl: null, text: "", href: "", isRazorpay: false });
        }

        if (e.target.tagName === "IMG") {
            if (selectedImgRef.current && selectedImgRef.current !== e.target) {
                selectedImgRef.current.style.outline = "";
            }
            const img = e.target;
            img.style.outline = "2px solid #9c27b0";
            selectedImgRef.current = img;
            setSelectedImg(img);
            const wPct = img.style.width && img.style.width.endsWith("%")
                ? parseInt(img.style.width, 10)
                : 100;
            setImgWidth(Number.isFinite(wPct) ? wPct : 100);
            return;
        }

        clearImgSelection();

        // clicking an existing link (that does not wrap a button) opens a small edit/remove popover
        const anchor = typeof e.target.closest === "function" ? e.target.closest("a") : null;
        if (anchor && editorRef.current?.contains(anchor) && !anchor.querySelector("button")) {
            e.preventDefault();
            setLinkPopover({ anchorEl: anchor, href: anchor.getAttribute("href") || "" });
        } else if (linkPopover.anchorEl) {
            setLinkPopover({ anchorEl: null, href: "" });
        }
    };

    const applyImgWidth = (pct) => {
        if (!selectedImg) return;
        selectedImg.style.width = `${pct}%`;
        selectedImg.style.height = "auto";
        setImgWidth(pct);
        touch();
    };

    const applyImgAlign = (align) => {
        if (!selectedImg) return;
        selectedImg.style.display = "block";
        selectedImg.style.float = "none";
        if (align === "left") {
            selectedImg.style.marginLeft = "0";
            selectedImg.style.marginRight = "auto";
        } else if (align === "center") {
            selectedImg.style.marginLeft = "auto";
            selectedImg.style.marginRight = "auto";
        } else {
            selectedImg.style.marginLeft = "auto";
            selectedImg.style.marginRight = "0";
        }
        touch();
    };

    const deleteSelectedImg = () => {
        if (!selectedImg) return;
        selectedImg.remove();
        clearImgSelection();
        handleContentInput();
    };

    const applyCover = (url, signedUrl) => {
        setCoverImageUrl(url);
        setCoverPreviewUrl(signedUrl);
        setErrors((prev) => ({ ...prev, coverImageUrl: "" }));
    };

    const closeCrop = () => {
        setCropState((prev) => {
            if (prev.objectUrl) URL.revokeObjectURL(prev.objectUrl);
            return { open: false, src: "", aspect: null, note: "", kind: null, file: null, objectUrl: null };
        });
    };

    // remember a local blob as the same-origin crop source for an image, keyed by
    // its data-url, so a later crop never has to read from the tainted
    // cross-origin remote URL.
    const cacheCropSource = (key, blob) => {
        if (!key || !blob) return;
        const objectUrl = URL.createObjectURL(blob);
        const prev = contentCropSrcRef.current.get(key);
        if (prev) {
            URL.revokeObjectURL(prev);
            objectUrlsRef.current = objectUrlsRef.current.filter((u) => u !== prev);
        }
        contentCropSrcRef.current.set(key, objectUrl);
        objectUrlsRef.current.push(objectUrl);
    };

    // resolve a same-origin (canvas-safe) source for cropping. Prefer this
    // session's cached local bytes; otherwise pull the remote image's bytes via
    // fetch() into a blob. Fetching gives a same-origin blob URL that the crop
    // canvas can export, and sidesteps the browser reusing a non-CORS cached copy
    // of the <img> shown in the editor (which would taint the canvas).
    const resolveCropSource = async (img) => {
        const key = img.getAttribute("data-url") || "";
        const cached = key ? contentCropSrcRef.current.get(key) : null;
        if (cached) return cached;

        const src = img.getAttribute("src") || "";
        if (!src || /^(blob:|data:)/i.test(src)) return src;

        try {
            const resp = await fetch(src, { mode: "cors", cache: "reload" });
            if (resp.ok) {
                const blob = await resp.blob();
                const objectUrl = URL.createObjectURL(blob);
                objectUrlsRef.current.push(objectUrl);
                if (key) contentCropSrcRef.current.set(key, objectUrl);
                return objectUrl;
            }
        } catch (e) {
            // host blocks cross-origin GET; fall back to the remote URL (the crop
            // export may then be blocked by the browser's CORS taint protection)
            console.error("Could not fetch image bytes for cropping:", e);
        }
        return src;
    };

    const openContentCrop = async () => {
        if (!selectedImg) return;
        const img = selectedImg;
        const src = await resolveCropSource(img);
        setCropState({
            open: true,
            src,
            aspect: null,
            note: "",
            kind: "content",
            file: null,
            objectUrl: null,
        });
    };

    // insert an <img> into the editor, restoring the caret if it was saved.
    // localBlob (when provided) is the same-origin source cached for re-cropping.
    const insertContentImage = (signedUrl, url, name = "image", localBlob = null) => {
        editorRef.current?.focus();
        const sel = window.getSelection();
        if (
            savedRangeRef.current &&
            editorRef.current?.contains(savedRangeRef.current.startContainer)
        ) {
            sel.removeAllRanges();
            sel.addRange(savedRangeRef.current);
        }
        document.execCommand(
            "insertHTML",
            false,
            `<img src="${signedUrl}" data-url="${url}" data-existing="false" alt="${name}" style="max-width:100%" />`
        );
        // cache the local bytes against this image's data-url so it stays
        // re-croppable without hitting the tainted cross-origin canvas path.
        if (localBlob) cacheCropSource(url, localBlob);
        handleContentInput();
    };

    // crop -> upload the cropped blob -> apply to the right target
    const handleCropConfirm = async (blob) => {
        try {
            setCropUploading(true);
            const file = new File([blob], "cropped.png", { type: "image/png" });
            const res = await uploadBlogImage(file);
            const url = res?.data?.url;
            const signedUrl = res?.data?.signedUrl || url;
            if (!url) {
                alert("Upload succeeded but no image URL was returned.");
                return;
            }
            if (cropState.kind === "content" && selectedImg) {
                selectedImg.setAttribute("src", signedUrl);
                selectedImg.setAttribute("data-url", url);
                selectedImg.setAttribute("data-existing", "false");
                selectedImg.style.width = "";
                setImgWidth(100);
                // keep the just-cropped bytes as the source for the next crop
                cacheCropSource(url, blob);
                handleContentInput();
            } else if (cropState.kind === "cover") {
                applyCover(url, signedUrl);
            } else if (cropState.kind === "content-new") {
                insertContentImage(signedUrl, url, "cropped.png", blob);
            }
            closeCrop();
        } catch (error) {
            console.error("Crop upload error:", error);
            alert(getApiErrorMessage(error, "Failed to save cropped image ❌"));
        } finally {
            setCropUploading(false);
        }
    };

    // "Use Full Image" from a pre-upload crop dialog -> upload the original untouched
    const handleUseOriginal = async () => {
        const original = cropState.file;
        if (!original) return;
        try {
            setCropUploading(true);
            const res = await uploadBlogImage(original);
            const url = res?.data?.url;
            const signedUrl = res?.data?.signedUrl || url;
            if (!url) {
                alert("Upload succeeded but no image URL was returned.");
                return;
            }
            if (cropState.kind === "cover") {
                applyCover(url, signedUrl);
            } else if (cropState.kind === "content-new") {
                insertContentImage(signedUrl, url, original.name, original);
            }
            closeCrop();
        } catch (error) {
            console.error("Image upload error:", error);
            alert(getApiErrorMessage(error, "Failed to upload image ❌"));
        } finally {
            setCropUploading(false);
        }
    };

    // upload cover / thumbnail image -> coverImageUrl
    const handleCoverUpload = async (e) => {
        const file = e.target.files?.[0];
        if (coverInputRef.current) coverInputRef.current.value = "";
        if (!file) return;

        // if the image is larger than the recommended cover resolution, offer the
        // crop tool (16:9) before uploading. object URLs are same-origin, so the
        // canvas export in the crop tool is never blocked by CORS.
        const dims = await readImageDimensions(file);
        if (dims && (dims.w > COVER_RECO.w || dims.h > COVER_RECO.h)) {
            const objectUrl = URL.createObjectURL(file);
            setCropState({
                open: true,
                src: objectUrl,
                aspect: COVER_ASPECT,
                note: `Your image is ${dims.w} × ${dims.h}px, larger than the recommended ${COVER_RECO.w} × ${COVER_RECO.h}px cover (16:9). Crop it to fit, or keep the full image.`,
                kind: "cover",
                file,
                objectUrl,
            });
            return;
        }

        try {
            setCoverUploading(true);
            const res = await uploadBlogImage(file);
            const url = res?.data?.url;
            const signedUrl = res?.data?.signedUrl || url;
            if (url) {
                applyCover(url, signedUrl);
            } else {
                alert("Upload succeeded but no image URL was returned.");
            }
        } catch (error) {
            console.error("Cover upload error:", error);
            alert(getApiErrorMessage(error, "Failed to upload cover image ❌"));
        } finally {
            setCoverUploading(false);
        }
    };

    // upload image and insert it inside the content editor
    const handleContentImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (contentImageInputRef.current) contentImageInputRef.current.value = "";
        if (!file) return;
        // remember where the caret is so we can insert there after any dialog
        saveSelection();

        // oversized inline images open the crop tool (freeform) before inserting
        const dims = await readImageDimensions(file);
        if (dims && (dims.w > CONTENT_RECO.w || dims.h > CONTENT_RECO.h)) {
            const objectUrl = URL.createObjectURL(file);
            setCropState({
                open: true,
                src: objectUrl,
                aspect: null,
                note: `Your image is ${dims.w} × ${dims.h}px. For faster page loads, keep content images within ${CONTENT_RECO.w} × ${CONTENT_RECO.h}px. Crop it, or insert the full image.`,
                kind: "content-new",
                file,
                objectUrl,
            });
            return;
        }

        try {
            setContentImageUploading(true);
            const res = await uploadBlogImage(file);
            const url = res?.data?.url;
            const signedUrl = res?.data?.signedUrl || url;
            if (url) {
                // show the signed (viewable) url in the editor, keep the plain url in data-url for submit
                insertContentImage(signedUrl, url, file.name, file);
            } else {
                alert("Upload succeeded but no image URL was returned.");
            }
        } catch (error) {
            console.error("Content image upload error:", error);
            alert(getApiErrorMessage(error, "Failed to upload image ❌"));
        } finally {
            setContentImageUploading(false);
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) {
            newErrors.title = "Title is required";
        } else if (formData.title.length > TITLE_MAX) {
            newErrors.title = `Title cannot exceed ${TITLE_MAX} characters`;
        }
        const html = (htmlMode ? rawHtml : (editorRef.current?.innerHTML || "")).trim();
        const text = (htmlMode
            ? rawHtml.replace(/<[^>]*>/g, "")
            : (editorRef.current?.innerText || "")
        ).trim();
        const hasMeaningfulContent =
            Boolean(text) ||
            (Boolean(html) && html !== "<p><br></p>" && html !== "<p></p>" && html !== "<br>");
        if (!hasMeaningfulContent) {
            newErrors.content = "Content is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // replace signed (preview) image urls with the plain stored url before submitting
    const getSubmitContent = () => {
        const rawContent = htmlMode ? rawHtml : (editorRef.current?.innerHTML || "");
        const processedHtml = decodeButtonHtml(rawContent);
        const container = document.createElement("div");
        container.innerHTML = processedHtml;
        container.querySelectorAll("img").forEach((img) => {
            const originalUrl = img.getAttribute("data-url");
            if (originalUrl) {
                img.setAttribute("src", originalUrl);
            }
            img.style.outline = "";
            img.removeAttribute("data-url");
            img.removeAttribute("data-existing");
        });
        // Wrap any stray Razorpay script in form
        container.querySelectorAll("script[data-payment_button_id], script[src*='payment-button.js']").forEach((script) => {
            if (!script.closest("form") || script.parentElement?.tagName !== "FORM") {
                const form = document.createElement("form");
                script.parentNode.insertBefore(form, script);
                form.appendChild(script);
            }
        });
        container.querySelectorAll("form").forEach((form) => {
            const script = form.querySelector("script[data-payment_button_id], script[src*='payment-button.js']");
            if (script) {
                form.querySelectorAll(".razorpay-payment-button").forEach((b) => b.remove());
            }
        });
        container.querySelectorAll("script").forEach((script) => {
            script.removeAttribute("data-executed");
        });
        container.querySelectorAll("button").forEach((btn) => {
            if (!btn.getAttribute("type")) {
                btn.setAttribute("type", "button");
            }
        });
        return container.innerHTML;
    };

    const handleSubmit = async (e) => {
        if (isEditMode && !hasChanges) {
            return;
        }
        e.preventDefault();
        clearImgSelection();
        if (!validate()) return;

        const payload = {
            title: formData.title.trim(),
            content: getSubmitContent(),
            contentFormat: "html",
            coverImageUrl: coverImageUrl || undefined,
            status: formData.status,
            slug: formData.slug.trim() || undefined,
        };

        try {
            setLoading(true);
            const res = isEditMode
                ? await updateBlog(id, payload)
                : await createBlog(payload);
            if (res?.success !== false) {
                alert(
                    res?.message ||
                    (isEditMode ? "Blog updated successfully ✅" : "Blog created successfully 🚀")
                );
                navigate(`/manage-blogs?page=${returnPage}`);
            }
        } catch (error) {
            console.error(isEditMode ? "Update blog error:" : "Create blog error:", error);
            alert(getApiErrorMessage(error, isEditMode ? "Failed to update blog ❌" : "Failed to create blog ❌"));
        } finally {
            setLoading(false);
        }
    };

    const currentNormalized = getNormalizedHtml(
        htmlMode ? rawHtml : (editorRef.current?.innerHTML || "")
    );
    const hasChanges =
        isEditMode && initialData
            ? initialData.title !== formData.title ||
            initialData.slug !== formData.slug ||
            initialData.status !== formData.status ||
            initialData.coverImageUrl !== coverImageUrl ||
            initialData.content !== currentNormalized
            : true;

    const titleLen = formData.title.length;
    const titleTooLong = titleLen > TITLE_MAX;

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f3f3f3", py: 4 }}>
            <Container maxWidth={false}>
                <Card
                    sx={{
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        border: "1px solid #ddd",
                        bgcolor: "#f7f7f7",
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
                                alignItems: "start",
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <Box>
                                <Button
                                    variant="contained"
                                    sx={{
                                        background: "linear-gradient(180deg, #c43dd6 0%, #9c27b0 100%)",
                                        textTransform: "uppercase",
                                        fontWeight: 600,
                                        px: 2.5,
                                        py: 1.2,
                                        borderRadius: "4px",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                    }}
                                    onClick={() =>
                                        navigate(isEditMode ? `/manage-blogs?page=${returnPage}` : "/dashboard")
                                    }
                                >
                                    {isEditMode ? "Back to Manage Blog" : "Back to Dashboard"}
                                </Button>
                            </Box>

                            <Typography
                                variant="h5"
                                sx={{
                                    textAlign: "center",
                                    fontWeight: 700,
                                    color: "#1f2937",
                                    mt: { xs: 1, md: 0 },
                                }}
                            >
                                {isEditMode ? "Edit Blog" : "Create Blog"}
                            </Typography>
                        </Box>

                        {fetching ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box
                                component="div"
                                sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}
                            >
                                <TextField
                                    fullWidth
                                    label="Title *"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleTitleChange}
                                    error={!!errors.title || titleTooLong}
                                    helperText={
                                        errors.title ||
                                        (titleTooLong
                                            ? `Title cannot exceed ${TITLE_MAX} characters`
                                            : `${titleLen}/${TITLE_MAX} characters`)
                                    }
                                />

                                <TextField
                                    fullWidth
                                    label="Slug"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleSlugChange}
                                    error={!!errors.slug}
                                    helperText={errors.slug || "Auto-generated from the title; you can edit it."}
                                />

                                {/* Cover / Thumbnail image */}
                                <Box
                                    sx={{
                                        border: "1px solid #d1d5db",
                                        borderRadius: "8px",
                                        p: 2,
                                        backgroundColor: "#fafafa",
                                    }}
                                >
                                    <Typography sx={{ fontSize: "16px", fontWeight: 600, mb: 1 }}>
                                        Cover / Thumbnail Image
                                    </Typography>

                                    {/* Recommended resolutions */}
                                    <Box
                                        sx={{
                                            mb: 1.5,
                                            p: 1.5,
                                            border: "1px solid #e5e7eb",
                                            borderRadius: "6px",
                                            backgroundColor: "#f9f5fb",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#6a1b9a", mb: 0.5 }}>
                                            Recommended image sizes
                                        </Typography>
                                        <Box sx={{ overflowX: "auto" }}>
                                            <Box
                                                component="table"
                                                sx={{
                                                    borderCollapse: "collapse",
                                                    fontSize: 12.5,
                                                    width: "100%",
                                                    "& th, & td": {
                                                        border: "1px solid #e5d5ee",
                                                        px: 1,
                                                        py: 0.5,
                                                        textAlign: "left",
                                                        whiteSpace: "nowrap",
                                                    },
                                                    "& th": { backgroundColor: "#f0e5f6", fontWeight: 600 },
                                                }}
                                            >
                                                <thead>
                                                    <tr>
                                                        <th>Use Case</th>
                                                        <th>Recommended Size</th>
                                                        <th>Aspect Ratio</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {RECOMMENDED_SIZES.map((row) => (
                                                        <tr key={row.use}>
                                                            <td>{row.use}</td>
                                                            <td>{row.size}</td>
                                                            <td>{row.ratio}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Box>
                                        </Box>
                                        <Typography sx={{ fontSize: 12, color: "#777", mt: 0.75 }}>
                                            Cover images larger than {COVER_RECO.w} × {COVER_RECO.h}px, and content images larger than {CONTENT_RECO.w} × {CONTENT_RECO.h}px, will open the crop tool so you can trim them to fit.
                                        </Typography>
                                    </Box>

                                    <input
                                        ref={coverInputRef}
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleCoverUpload}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<ImageIcon />}
                                        disabled={coverUploading}
                                        onClick={() => coverInputRef.current?.click()}
                                        sx={{ textTransform: "none" }}
                                    >
                                        {coverUploading ? "Uploading..." : "Upload Cover Image"}
                                    </Button>
                                    {coverPreviewUrl && (
                                        <Box sx={{ mt: 2 }}>
                                            <img
                                                src={coverPreviewUrl}
                                                alt="cover preview"
                                                style={{
                                                    maxWidth: "240px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #e5e7eb",
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {/* Content editor */}
                                <Box
                                    sx={{
                                        border: errors.content ? "1px solid #d32f2f" : "1px solid #d1d5db",
                                        borderRadius: "8px",
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 1,
                                            flexWrap: "wrap",
                                            p: 1,
                                            borderBottom: "1px solid #e5e7eb",
                                            backgroundColor: "#fafafa",
                                            alignItems: "center",
                                        }}
                                    >
                                        <TBtn title="Bold" active={activeFormats.bold} disabled={htmlMode} onClick={() => exec("bold")}>
                                            <FormatBold fontSize="small" />
                                        </TBtn>
                                        <TBtn title="Italic" active={activeFormats.italic} disabled={htmlMode} onClick={() => exec("italic")}>
                                            <FormatItalic fontSize="small" />
                                        </TBtn>
                                        <TBtn
                                            title="Underline"
                                            active={activeFormats.underline}
                                            disabled={htmlMode}
                                            onClick={() => exec("underline")}
                                        >
                                            <FormatUnderlined fontSize="small" />
                                        </TBtn>

                                        <Divider orientation="vertical" flexItem />

                                        <TBtn
                                            title="Heading"
                                            active={activeFormats.h1}
                                            disabled={htmlMode}
                                            onClick={() => toggleBlock("<h1>")}
                                        >
                                            <TitleIcon fontSize="small" />
                                        </TBtn>
                                        <TBtn
                                            title="Block quote"
                                            active={activeFormats.blockquote}
                                            disabled={htmlMode}
                                            onClick={() => toggleBlock("<blockquote>")}
                                        >
                                            <FormatQuote fontSize="small" />
                                        </TBtn>

                                        <Divider orientation="vertical" flexItem />

                                        <TBtn
                                            title="Bulleted list"
                                            active={activeFormats.ul}
                                            disabled={htmlMode}
                                            onClick={() => exec("insertUnorderedList")}
                                        >
                                            <FormatListBulleted fontSize="small" />
                                        </TBtn>
                                        <TBtn
                                            title="Numbered list"
                                            active={activeFormats.ol}
                                            disabled={htmlMode}
                                            onClick={() => exec("insertOrderedList")}
                                        >
                                            <FormatListNumbered fontSize="small" />
                                        </TBtn>

                                        <Divider orientation="vertical" flexItem />

                                        <TBtn
                                            title="Align left"
                                            active={activeFormats.justifyLeft}
                                            disabled={htmlMode}
                                            onClick={() => exec("justifyLeft")}
                                        >
                                            <FormatAlignLeft fontSize="small" />
                                        </TBtn>
                                        <TBtn
                                            title="Align center"
                                            active={activeFormats.justifyCenter}
                                            disabled={htmlMode}
                                            onClick={() => exec("justifyCenter")}
                                        >
                                            <FormatAlignCenter fontSize="small" />
                                        </TBtn>
                                        <TBtn
                                            title="Align right"
                                            active={activeFormats.justifyRight}
                                            disabled={htmlMode}
                                            onClick={() => exec("justifyRight")}
                                        >
                                            <FormatAlignRight fontSize="small" />
                                        </TBtn>

                                        <Divider orientation="vertical" flexItem />

                                        <TBtn title="Insert link" disabled={htmlMode} onClick={openLinkDialog}>
                                            <LinkIcon fontSize="small" />
                                        </TBtn>
                                        <TBtn title="Emoji" disabled={htmlMode} onClick={openEmoji}>
                                            <EmojiEmotions fontSize="small" />
                                        </TBtn>
                                        <TBtn title="Insert table" disabled={htmlMode} onClick={openTable}>
                                            <TableChart fontSize="small" />
                                        </TBtn>
                                        <TBtn title="Page break" disabled={htmlMode} onClick={insertPageBreak}>
                                            <HorizontalRule fontSize="small" />
                                        </TBtn>
                                        <TBtn title="Add / Edit Button" onClick={openAddButtonDialog}>
                                            <SmartButtonIcon fontSize="small" />
                                        </TBtn>
                                        <TBtn
                                            title={htmlMode ? "Switch to Visual Editor" : "HTML Source Code (</>)"}
                                            active={htmlMode}
                                            onClick={toggleHtmlMode}
                                        >
                                            <CodeIcon fontSize="small" />
                                        </TBtn>

                                        <Divider orientation="vertical" flexItem />

                                        <input
                                            ref={contentImageInputRef}
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={handleContentImageUpload}
                                        />
                                        <Button
                                            sx={{ ...toolbarBtn, minWidth: "auto", px: 1.5 }}
                                            disabled={contentImageUploading || htmlMode}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => contentImageInputRef.current?.click()}
                                        >
                                            {contentImageUploading ? (
                                                <CircularProgress size={18} />
                                            ) : (
                                                <>
                                                    <ImageIcon fontSize="small" />
                                                    <Typography sx={{ ml: 0.5, fontSize: 13, textTransform: "none" }}>
                                                        Image
                                                    </Typography>
                                                </>
                                            )}
                                        </Button>
                                    </Box>

                                    {/* Image editing toolbar - shown when an image is selected */}
                                    {selectedImg && !htmlMode && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1.5,
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                p: 1,
                                                borderBottom: "1px solid #e5e7eb",
                                                backgroundColor: "#f3e5f5",
                                            }}
                                        >
                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#6a1b9a" }}>
                                                Image:
                                            </Typography>
                                            <Tooltip title="Align left" arrow>
                                                <IconButton size="small" onClick={() => applyImgAlign("left")}>
                                                    <FormatAlignLeft fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Align center" arrow>
                                                <IconButton size="small" onClick={() => applyImgAlign("center")}>
                                                    <FormatAlignCenter fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Align right" arrow>
                                                <IconButton size="small" onClick={() => applyImgAlign("right")}>
                                                    <FormatAlignRight fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 180 }}>
                                                <Typography sx={{ fontSize: 12, color: "#6a1b9a" }}>Size</Typography>
                                                <Slider
                                                    size="small"
                                                    min={10}
                                                    max={100}
                                                    value={imgWidth}
                                                    onChange={(_, v) => applyImgWidth(v)}
                                                    sx={{ width: 120, color: "#9c27b0" }}
                                                />
                                                <Typography sx={{ fontSize: 12, color: "#6a1b9a", width: 34 }}>
                                                    {imgWidth}%
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Crop image" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={openContentCrop}
                                                    disabled={!selectedImg.getAttribute("src")}
                                                >
                                                    <Crop fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete image" arrow>
                                                <IconButton size="small" onClick={deleteSelectedImg} color="error">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}

                                    {htmlMode ? (
                                        <Box sx={{ p: 2, backgroundColor: "#1e1e1e", borderRadius: "0 0 8px 8px" }}>
                                            <Typography sx={{ fontSize: 12, color: "#9ca3af", mb: 1, fontFamily: "monospace" }}>
                                                HTML Source Code Mode — You can paste or write raw HTML like &lt;button type="button"&gt;Click Me!&lt;/button&gt; directly:
                                            </Typography>
                                            <TextField
                                                multiline
                                                fullWidth
                                                minRows={10}
                                                maxRows={22}
                                                value={rawHtml}
                                                onChange={handleRawHtmlChange}
                                                placeholder='<p>Write or paste your HTML here, e.g. <button type="button">Click Me!</button></p>'
                                                InputProps={{
                                                    sx: {
                                                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                                        fontSize: "14px",
                                                        color: "#f8f8f2",
                                                        backgroundColor: "#272822",
                                                        borderRadius: "4px",
                                                        lineHeight: 1.5,
                                                        "& textarea": {
                                                            color: "#f8f8f2",
                                                        },
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box
                                            ref={editorRef}
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={handleContentInput}
                                            onClick={handleEditorClick}
                                            onPaste={handlePaste}
                                            onKeyUp={updateActiveFormats}
                                            onMouseUp={updateActiveFormats}
                                            sx={{
                                                minHeight: 220,
                                                maxHeight: 460,
                                                overflowY: "auto",
                                                p: 2,
                                                outline: "none",
                                                fontSize: "15px",
                                                lineHeight: 1.6,
                                                "& img": { maxWidth: "100%" },
                                                "& button": {
                                                    cursor: "pointer",
                                                    padding: "8px 18px",
                                                    borderRadius: "6px",
                                                    border: "1px solid #9c27b0",
                                                    backgroundColor: "#9c27b0",
                                                    color: "#ffffff",
                                                    fontSize: "14px",
                                                    fontWeight: 600,
                                                    display: "inline-block",
                                                    margin: "6px 2px",
                                                    verticalAlign: "middle",
                                                    fontFamily: "inherit",
                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                                                    transition: "all 0.2s ease-in-out",
                                                    "&:hover": {
                                                        backgroundColor: "#7b1fa2",
                                                        borderColor: "#7b1fa2",
                                                    },
                                                },
                                                "& blockquote": {
                                                    borderLeft: "4px solid #9c27b0",
                                                    margin: "12px 0",
                                                    padding: "4px 16px",
                                                    color: "#555",
                                                    fontStyle: "italic",
                                                    background: "#faf5fc",
                                                },
                                                "& table": { borderCollapse: "collapse", width: "100%" },
                                                "& td": { border: "1px solid #ccc", padding: "8px" },
                                                "& a": { color: "#1e88e5", textDecoration: "underline" },
                                                "&:empty:before": {
                                                    content: '"Write your blog content here..."',
                                                    color: "#9ca3af",
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                                {errors.content && (
                                    <Typography sx={{ color: "#d32f2f", fontSize: 12, mt: -1.5, ml: 1 }}>
                                        {errors.content}
                                    </Typography>
                                )}

                                <TextField
                                    fullWidth
                                    select
                                    label="Status *"
                                    name="status"
                                    value={formData.status}
                                    onChange={(e) =>
                                        setFormData((prev) => ({ ...prev, status: e.target.value }))
                                    }
                                >
                                    <MenuItem value="draft">Draft</MenuItem>
                                    <MenuItem value="published">Published</MenuItem>
                                </TextField>

                                <Box sx={{ mt: 1 }}>
                                    <Button
                                        type="button"
                                        onClick={handleSubmit}
                                        variant="contained"
                                        disabled={loading || titleTooLong || (isEditMode && !hasChanges)}
                                        sx={{
                                            backgroundColor: "#1e88e5",
                                            textTransform: "uppercase",
                                            fontWeight: 600,
                                            px: 3,
                                            py: 1.2,
                                        }}
                                    >
                                        {loading ? (
                                            <CircularProgress size={24} color="inherit" />
                                        ) : isEditMode ? (
                                            "Update Blog"
                                        ) : (
                                            "Create Blog"
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>

            {/* Emoji picker */}
            <Popover
                open={Boolean(emojiAnchor)}
                anchorEl={emojiAnchor}
                onClose={() => setEmojiAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box
                    sx={{
                        p: 1.5,
                        display: "grid",
                        gridTemplateColumns: "repeat(8, 34px)",
                        gap: 0.5,
                        maxHeight: 260,
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    {EMOJIS.map((em) => (
                        <Box
                            key={em}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => insertEmoji(em)}
                            sx={{
                                cursor: "pointer",
                                fontSize: 20,
                                lineHeight: "30px",
                                height: 30,
                                textAlign: "center",
                                borderRadius: "4px",
                                "&:hover": { backgroundColor: "#f3e5f5" },
                            }}
                        >
                            {em}
                        </Box>
                    ))}
                </Box>
            </Popover>

            {/* Table size picker */}
            <Popover
                open={Boolean(tableAnchor)}
                anchorEl={tableAnchor}
                onClose={() => setTableAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box sx={{ p: 1.5 }}>
                    <Box
                        onMouseLeave={() => setTableHover({ r: 0, c: 0 })}
                        sx={{ display: "grid", gridTemplateColumns: "repeat(8, 18px)", gap: "3px" }}
                    >
                        {Array.from({ length: 8 }).map((_, r) =>
                            Array.from({ length: 8 }).map((__, c) => {
                                const on = r < tableHover.r && c < tableHover.c;
                                return (
                                    <Box
                                        key={`${r}-${c}`}
                                        onMouseEnter={() => setTableHover({ r: r + 1, c: c + 1 })}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => insertTable(r + 1, c + 1)}
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            border: "1px solid #cbb2d6",
                                            borderRadius: "2px",
                                            cursor: "pointer",
                                            backgroundColor: on ? "#9c27b0" : "#fff",
                                        }}
                                    />
                                );
                            })
                        )}
                    </Box>
                    <Typography sx={{ mt: 1, fontSize: 12, textAlign: "center", color: "#555" }}>
                        {tableHover.r > 0 ? `${tableHover.r} × ${tableHover.c}` : "Select size"}
                    </Typography>
                </Box>
            </Popover>

            {/* Link edit/remove popover (shown when an existing link is clicked) */}
            <Popover
                open={Boolean(linkPopover.anchorEl)}
                anchorEl={linkPopover.anchorEl}
                onClose={() => setLinkPopover({ anchorEl: null, href: "" })}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1, maxWidth: 320 }}>
                    <Typography sx={{ fontSize: 13, color: "#374151", wordBreak: "break-all" }}>
                        <LinkIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: "middle" }} />
                        {linkPopover.href || "(no URL)"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openEditLink(linkPopover.anchorEl)}
                            sx={{ textTransform: "none" }}
                        >
                            Edit
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => removeLink(linkPopover.anchorEl)}
                            sx={{ textTransform: "none" }}
                        >
                            Remove
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {/* Link dialog */}
            <Dialog open={linkOpen} onClose={closeLinkDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{linkEditing ? "Edit Link" : "Insert Link"}</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
                    {!linkHasSelection && (
                        <TextField
                            label="Text to display"
                            size="small"
                            value={linkText}
                            onChange={(e) => setLinkText(e.target.value)}
                            fullWidth
                        />
                    )}
                    <TextField
                        label="URL"
                        size="small"
                        placeholder="https://example.com"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        fullWidth
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                applyLink();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    {linkEditing && (
                        <Button onClick={() => removeLink()} color="error" sx={{ mr: "auto" }}>
                            Remove
                        </Button>
                    )}
                    <Button onClick={closeLinkDialog}>Cancel</Button>
                    <Button onClick={applyLink} variant="contained" disabled={!linkUrl.trim()}>
                        {linkEditing ? "Update" : "Insert"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Button click popover (Edit | Remove) */}
            <Popover
                open={Boolean(buttonPopover.anchorEl)}
                anchorEl={buttonPopover.anchorEl}
                onClose={() => setButtonPopover({ anchorEl: null, btnEl: null, text: "", href: "" })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1, minWidth: 220, maxWidth: 340 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
                        Button: "{buttonPopover.text}"
                    </Typography>
                    {buttonPopover.href && (
                        <Typography sx={{ fontSize: 12, color: "#6b7280", wordBreak: "break-all" }}>
                            <LinkIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: "middle" }} />
                            {buttonPopover.href}
                        </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 0.5 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openEditButton(buttonPopover.btnEl)}
                            sx={{ textTransform: "none" }}
                        >
                            Edit
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => removeButton(buttonPopover.btnEl)}
                            sx={{ textTransform: "none" }}
                        >
                            Remove
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {/* Add / Edit Button Dialog */}
            <Dialog open={buttonDialogOpen} onClose={() => setButtonDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{buttonEditingEl ? "Edit Button" : "Add Button"}</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
                    {/* Style Variant selection */}
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 1 }}>
                            Button Style:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {Object.entries(BUTTON_VARIANTS).map(([key, item]) => (
                                <Button
                                    key={key}
                                    size="small"
                                    variant={buttonVariant === key ? "contained" : "outlined"}
                                    onClick={() => setButtonVariant(key)}
                                    sx={{
                                        textTransform: "none",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        ...(buttonVariant === key
                                            ? { backgroundColor: "#9c27b0", color: "#fff", "&:hover": { backgroundColor: "#7b1fa2" } }
                                            : { borderColor: "#d1d5db", color: "#374151" }),
                                    }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                            <Button
                                size="small"
                                variant={buttonVariant === "custom" ? "contained" : "outlined"}
                                onClick={() => setButtonVariant("custom")}
                                sx={{
                                    textTransform: "none",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    ...(buttonVariant === "custom"
                                        ? { backgroundColor: "#9c27b0", color: "#fff", "&:hover": { backgroundColor: "#7b1fa2" } }
                                        : { borderColor: "#d1d5db", color: "#374151" }),
                                }}
                            >
                                Custom HTML
                            </Button>
                        </Box>
                    </Box>

                    {buttonVariant === "razorpay" ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <TextField
                                label="Razorpay Button ID or Embed Code *"
                                size="small"
                                placeholder='pl_TnQqnBysBUVrCG or <form><script src="https://checkout.razorpay.com/v1/payment-button.js" data-payment_button_id="pl_TnQqnBysBUVrCG" async></script></form>'
                                value={razorpayButtonId}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const match = val.match(/data-payment_button_id=["']([^"']+)["']/i);
                                    setRazorpayButtonId(match ? match[1] : val);
                                }}
                                fullWidth
                                autoFocus
                                helperText="Enter your Razorpay Button ID or paste the full <form><script> tag snippet provided by Razorpay."
                            />
                        </Box>
                    ) : buttonVariant === "custom" ? (
                        <TextField
                            label="Custom HTML Code"
                            size="small"
                            multiline
                            rows={4}
                            value={buttonCustomHtml}
                            onChange={(e) => setButtonCustomHtml(e.target.value)}
                            placeholder='<button type="button" style="background:#ff5722;color:#fff;padding:10px 20px;border:none;border-radius:4px;cursor:pointer;">Custom Action</button>'
                            fullWidth
                            InputProps={{
                                sx: { fontFamily: 'Consolas, Monaco, monospace', fontSize: "13px" },
                            }}
                        />
                    ) : (
                        <>
                            <TextField
                                label="Button Text *"
                                size="small"
                                placeholder="Click Me!"
                                value={buttonText}
                                onChange={(e) => setButtonText(e.target.value)}
                                fullWidth
                                autoFocus
                            />
                            <TextField
                                label="Link URL (optional)"
                                size="small"
                                placeholder="https://example.com or /tournaments"
                                value={buttonUrl}
                                onChange={(e) => setButtonUrl(e.target.value)}
                                fullWidth
                                helperText="Leave blank if this button does not navigate to a URL."
                            />
                            {buttonUrl.trim() && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={buttonNewTab}
                                            onChange={(e) => setButtonNewTab(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: 13 }}>Open link in new tab</Typography>}
                                />
                            )}
                        </>
                    )}

                    {/* Live Preview */}
                    <Box
                        sx={{
                            p: 2,
                            border: "1px dashed #9c27b0",
                            borderRadius: "6px",
                            backgroundColor: "#faf5fc",
                            textAlign: "center",
                            minHeight: 80,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6a1b9a", mb: 1, textAlign: "left" }}>
                            Live Preview:
                        </Typography>
                        {buttonVariant === "razorpay" ? (
                            <Box
                                ref={dialogPreviewRef}
                                key={razorpayButtonId}
                                dangerouslySetInnerHTML={{
                                    __html: `<form><script src="https://checkout.razorpay.com/v1/payment-button.js" data-payment_button_id="${(razorpayButtonId.match(/data-payment_button_id=["']([^"']+)["']/i)?.[1] || razorpayButtonId.trim()) || "pl_TnQqnBysBUVrCG"
                                        }" async> </script></form>`,
                                }}
                            />
                        ) : buttonVariant === "custom" ? (
                            <Box dangerouslySetInnerHTML={{ __html: buttonCustomHtml || "<em>Enter custom HTML above</em>" }} />
                        ) : (
                            <Box>
                                <button
                                    type="button"
                                    style={{
                                        ...(BUTTON_VARIANTS[buttonVariant]?.style
                                            ? Object.fromEntries(
                                                BUTTON_VARIANTS[buttonVariant].style
                                                    .split(";")
                                                    .filter(Boolean)
                                                    .map((s) => {
                                                        const [k, v] = s.split(":");
                                                        const camelK = k.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                                                        return [camelK, v.trim()];
                                                    })
                                            )
                                            : {}),
                                    }}
                                >
                                    {buttonText || "Click Me!"}
                                </button>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    {buttonEditingEl && (
                        <Button
                            onClick={() => {
                                removeButton(buttonEditingEl);
                                setButtonDialogOpen(false);
                            }}
                            color="error"
                            sx={{ mr: "auto" }}
                        >
                            Remove
                        </Button>
                    )}
                    <Button onClick={() => setButtonDialogOpen(false)}>Cancel</Button>
                    <Button onClick={applyButton} variant="contained" sx={{ backgroundColor: "#9c27b0", "&:hover": { backgroundColor: "#7b1fa2" } }}>
                        {buttonEditingEl ? "Update Button" : "Insert Button"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Crop modal (shared by cover-upload and content-image cropping) */}
            <CropModal
                open={cropState.open}
                src={cropState.src}
                aspect={cropState.aspect}
                note={cropState.note}
                uploading={cropUploading}
                onCancel={closeCrop}
                onConfirm={handleCropConfirm}
                onUseOriginal={cropState.file ? handleUseOriginal : null}
            />
        </Box>
    );
};

export default CreateBlog;
